import { LocalDate } from "@js-joda/core";
import { createPortal } from "react-dom";
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { ZIndex } from "config/zIndex";
import {
  UNMEASURED_ANCHORED_POSITION,
  type AnchoredPosition,
} from "components/designV2/anchoredPosition.logic";
import {
  getCalendarLabels,
  getStockholmLocalDateNow,
} from "components/designV2/datePicker/datePicker.utils";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { useParentZIndex } from "components/designV2/stacking/stackingZIndexContext";
import { useAnchoredPosition } from "components/designV2/useAnchoredPosition";
import {
  Container,
  MonthButton,
  MonthGrid,
  MonthLabel,
  NavButton,
  PeriodButton,
  Popup,
  PopupHeader,
  PopupYear,
  TodayButton,
  YearNavButton,
} from "./monthPeriodNavigator.styled";

const MIN_YEAR = 2000;

interface MonthBoundaryParams {
  readonly boundaryDate: LocalDate;
  readonly date: LocalDate;
}

interface OptionalMonthBoundaryParams {
  readonly boundaryDate?: LocalDate;
  readonly date: LocalDate;
}

const isSameMonth = (params: MonthBoundaryParams): boolean =>
  params.date.year() === params.boundaryDate.year() &&
  params.date.monthValue() === params.boundaryDate.monthValue();

const isMonthAfterBoundary = (params: OptionalMonthBoundaryParams): boolean =>
  params.boundaryDate != null &&
  (params.date.year() > params.boundaryDate.year() ||
    (params.date.year() === params.boundaryDate.year() &&
      params.date.monthValue() > params.boundaryDate.monthValue()));

const isMonthBeforeBoundary = (params: MonthBoundaryParams): boolean =>
  params.date.year() < params.boundaryDate.year() ||
  (params.date.year() === params.boundaryDate.year() &&
    params.date.monthValue() < params.boundaryDate.monthValue());

export interface MonthPeriodNavigatorProps {
  readonly currentDate: LocalDate;
  readonly maxDate?: LocalDate;
  readonly minDate?: LocalDate;
  readonly showTodayButton?: boolean;
  readonly onChange: (nextDate: LocalDate) => void;
}

interface PeriodPickerPopupProps {
  readonly currentMonth: number;
  readonly currentYear: number;
  readonly draftYear: number;
  readonly isMeasured: boolean;
  readonly maxDate?: LocalDate;
  readonly minDate: LocalDate;
  readonly monthNames: readonly string[];
  readonly position: AnchoredPosition;
  readonly popupRef: RefObject<HTMLDivElement>;
  readonly popupZIndex: number;
  readonly onMonthSelect: (month: number) => void;
  readonly onYearChange: (year: number) => void;
}

const PeriodPickerPopup = (props: PeriodPickerPopupProps): JSX.Element => {
  const { t } = useTranslation();
  const isPreviousYearDisabled = props.draftYear <= props.minDate.year();
  const isNextYearDisabled =
    props.maxDate != null && props.draftYear >= props.maxDate.year();

  return createPortal(
    <Popup
      ref={props.popupRef}
      role="dialog"
      aria-label={t("DatePicker.SelectPeriod")}
      $left={props.position.left}
      $top={props.position.top}
      $zIndex={props.popupZIndex}
      $isMeasured={props.isMeasured}
    >
      <PopupHeader>
        <YearNavButton
          type="button"
          aria-label={t("DatePicker.PreviousYear")}
          disabled={isPreviousYearDisabled}
          onClick={() => props.onYearChange(props.draftYear - 1)}
        >
          <CustomIcon
            name="v2/left-chevron"
            size="16px"
            color={isPreviousYearDisabled ? ColorV2.Blue5 : ColorV2.Blue2}
          />
        </YearNavButton>
        <PopupYear>{props.draftYear}</PopupYear>
        <YearNavButton
          type="button"
          aria-label={t("DatePicker.NextYear")}
          disabled={isNextYearDisabled}
          onClick={() => props.onYearChange(props.draftYear + 1)}
        >
          <CustomIcon
            name="v2/right-chevron"
            size="16px"
            color={isNextYearDisabled ? ColorV2.Blue5 : ColorV2.Blue2}
          />
        </YearNavButton>
      </PopupHeader>
      <MonthGrid>
        {props.monthNames.map((name, index) => (
          <MonthButton
            key={name}
            type="button"
            disabled={
              isMonthAfterBoundary({
                boundaryDate: props.maxDate,
                date: LocalDate.of(props.draftYear, index + 1, 1),
              }) ||
              isMonthBeforeBoundary({
                boundaryDate: props.minDate,
                date: LocalDate.of(props.draftYear, index + 1, 1),
              })
            }
            $isSelected={
              props.draftYear === props.currentYear &&
              index + 1 === props.currentMonth
            }
            aria-label={name}
            aria-pressed={
              props.draftYear === props.currentYear &&
              index + 1 === props.currentMonth
            }
            onClick={() => props.onMonthSelect(index + 1)}
          >
            {name.slice(0, 3)}
          </MonthButton>
        ))}
      </MonthGrid>
    </Popup>,
    document.body,
  );
};

export const MonthPeriodNavigator = (
  props: MonthPeriodNavigatorProps,
): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [draftYear, setDraftYear] = useState(props.currentDate.year());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const parentZIndex = useParentZIndex();
  const popupZIndex = parentZIndex != null ? parentZIndex + 1 : ZIndex.dropdown;
  const position = useAnchoredPosition({
    floatingRef: popupRef,
    isOpen,
    minWidth: 264,
    triggerRef,
    widthMode: "content",
  });

  useEffect(() => {
    setDraftYear(props.currentDate.year());
  }, [props.currentDate]);

  const closePicker = useCallback((): void => {
    setIsOpen(false);
  }, []);

  useOnClickOutside([triggerRef, popupRef], closePicker, {
    enabled: isOpen,
    eventType: "mousedown",
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") closePicker();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closePicker, isOpen]);

  const { monthNames } = useMemo(() => getCalendarLabels(t), [t]);
  const previousDate = props.currentDate.minusMonths(1);
  const nextDate = props.currentDate.plusMonths(1);
  const monthName = monthNames[props.currentDate.monthValue() - 1] ?? "";
  const today = getStockholmLocalDateNow();
  const minDate = props.minDate ?? LocalDate.of(MIN_YEAR, 1, 1);
  const isCurrentMonth = isSameMonth({
    boundaryDate: today,
    date: props.currentDate,
  });
  const isPreviousMonthDisabled = isMonthBeforeBoundary({
    boundaryDate: minDate,
    date: previousDate,
  });
  const isNextMonthDisabled = isMonthAfterBoundary({
    boundaryDate: props.maxDate,
    date: nextDate,
  });
  const isTodayDisabled =
    isCurrentMonth ||
    isMonthBeforeBoundary({ boundaryDate: minDate, date: today }) ||
    isMonthAfterBoundary({ boundaryDate: props.maxDate, date: today });

  const selectMonth = (month: number): void => {
    const nextMonth = LocalDate.of(draftYear, month, 1);
    if (
      isMonthBeforeBoundary({ boundaryDate: minDate, date: nextMonth }) ||
      isMonthAfterBoundary({
        boundaryDate: props.maxDate,
        date: nextMonth,
      })
    ) {
      return;
    }
    props.onChange(nextMonth);
    closePicker();
  };

  const togglePicker = (): void => {
    if (!isOpen) setDraftYear(props.currentDate.year());
    setIsOpen((open) => !open);
  };

  return (
    <>
      <Container>
        <NavButton
          type="button"
          aria-label={t("DatePicker.PreviousMonth")}
          disabled={isPreviousMonthDisabled}
          onClick={() => props.onChange(previousDate)}
        >
          <CustomIcon
            name="v2/left-chevron"
            size="20px"
            color={ColorV2.Blue2}
          />
        </NavButton>
        <PeriodButton
          ref={triggerRef}
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={t("DatePicker.SelectPeriod")}
          onClick={togglePicker}
        >
          <MonthLabel>
            {monthName} {props.currentDate.year()}
          </MonthLabel>
          <MonthLabel $isSubLabel>
            1 - {props.currentDate.lengthOfMonth()} {monthName}
          </MonthLabel>
        </PeriodButton>
        {props.showTodayButton && (
          <TodayButton
            type="button"
            aria-label={t("DatePicker.Today")}
            disabled={isTodayDisabled}
            onClick={() => {
              props.onChange(today);
              closePicker();
            }}
          >
            {t("DatePicker.Today")}
          </TodayButton>
        )}
        <NavButton
          type="button"
          aria-label={t("DatePicker.NextMonth")}
          disabled={isNextMonthDisabled}
          onClick={() => props.onChange(nextDate)}
        >
          <CustomIcon
            name="v2/right-chevron"
            size="20px"
            color={ColorV2.Blue2}
          />
        </NavButton>
      </Container>
      {isOpen && typeof document !== "undefined" ? (
        <PeriodPickerPopup
          popupRef={popupRef}
          currentMonth={props.currentDate.monthValue()}
          currentYear={props.currentDate.year()}
          draftYear={draftYear}
          isMeasured={position != null}
          maxDate={props.maxDate}
          minDate={minDate}
          monthNames={monthNames}
          position={position ?? UNMEASURED_ANCHORED_POSITION}
          popupZIndex={popupZIndex}
          onMonthSelect={selectMonth}
          onYearChange={setDraftYear}
        />
      ) : null}
    </>
  );
};
